/**
 * 知识库管理 API
 * 
 * 后端路由: /api/v1/kb/*
 * 对应后端: apps/agent-service/src/knowledge/router.py
 */
import type {
  CreateKnowledgeBaseForm,
  KnowledgeBase,
  KnowledgeBaseGroup,
  KnowledgeIndexingTechnique,
  KnowledgeProcessRule,
  KnowledgeRetrievalModel,
  KnowledgeBaseType
} from '@/types/knowledge-base'
import apiClient from './index'
import { getProjectId, getUserId, tokenHolder } from '@/utils'
import { logger } from '@/utils/logger'

// ========================= 基础路径 =========================
const BASE_PATH = '/kb'

// ========================= Dify 风格检索类型 =========================
/** Dify 风格检索方式（**单选**），与后端 ``RetrievalMethod`` 对齐 */
export type RetrievalMethod =
  | 'semantic_search'
  | 'keyword_search'
  | 'full_text_search'
  | 'hybrid_search'

/** Dify 风格重排模式（与后端 ``RerankMode`` 对齐） */
export type RerankMode = 'reranking_model' | 'weighted_score'

/** 加权融合权重配置（仅 WEIGHTED_SCORE 模式生效，与后端 ``WeightsConfig`` 对齐） */
export interface WeightsConfig {
  vector_weight: number
  keyword_weight: number
}

export interface KnowledgeSyncResult {
  success: boolean
  total_checked: number
  created_count: number
  updated_count: number
  deleted_count: number
  skipped_count: number
  failed_count: number
  errors: string[]
  started_at: string
  finished_at: string
  duration_ms: number
}

export interface GitDocsVersionStatus {
  is_latest: boolean
  pending_updates: number
  created_paths: string[]
  updated_paths: string[]
  deleted_paths: string[]
  last_commit?: string | null
  source_id: string
  repo_url: string
  branch: string
}

export interface GitDocsSyncTaskStatus {
  task_id: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'interrupted' | 'cancelled' | string
  message: string
  phase?: string | null
  progress: number
  created_count: number
  updated_count: number
  deleted_count: number
  skipped_count: number
  failed_count: number
  total_checked: number
  changed_paths: string[]
  errors: string[]
  started_at: string
  finished_at?: string | null
  batch_total?: number
  batch_succeeded?: number
  batch_failed?: number
}

/** Dify 风格检索选项（推荐使用，覆盖老的 searchModes 字段） */
export interface DifyRetrievalOptions {
  /** **Dify 风格** 单选检索方式（first-class，优先于 searchModes） */
  retrievalMethod?: RetrievalMethod
  /** 重排模式：默认 reranking_model */
  rerankingMode?: RerankMode
  /** WEIGHTED_SCORE 权重；默认 0.7/0.3（与 Dify 默认对齐） */
  weights?: WeightsConfig
}

export interface DocumentSearchResultItem {
  rank: number
  type: 'text' | 'attachment'
  id: string
  resource_id?: string
  file_name?: string
  content: string
  file_path?: string
  heading?: string | null
  chapter_path?: string | null
  document_url?: string | null
  highlight_terms?: string[]
  matched_chunks?: number
  matched_headings?: string[]
  matched_chapter_paths?: string[]
  attachment_type?: string
  semantic_score: number | null
  keyword_score: number | null
  hybrid_score: number | null
  final_score: number | null
  content_preview?: string
  word_count?: number
  keywords?: string[]
  attachments?: Array<{ url: string; original_url?: string; caption: string | null }>
}

export interface DocumentSearchResponse {
  query_id?: string | null
  query: string
  search_modes: string[]
  retrieval_method?: RetrievalMethod | null
  reranking_mode?: RerankMode | null
  enable_rerank?: boolean
  rewritten_query?: string | null
  query_intent?: string | null
  answer_style?: string | null
  target_document_type?: string | null
  answer?: string | null
  answer_mode?: 'disabled' | 'llm' | 'extractive' | string
  answer_evidence?: Array<{
    citation_id: string
    source_rank: number
    file_name: string
    file_path: string
    chapter_path: string
    snippet: string
    document_url?: string | null
  }>
  citation_validation?: {
    is_valid: boolean
    missing_citations: boolean
    unknown_citations: string[]
    used_citations: string[]
    allowed_citations: string[]
  }
  debug_info?: {
    original_query?: string
    rewritten_query?: string
    query_intent?: string
    answer_style?: string
    retrieval_method?: string
    reranking_mode?: string
    rerank_model?: string | null
    enable_rerank?: boolean
    recall_candidates?: DocumentSearchDebugItem[]
    reranked_candidates?: DocumentSearchDebugItem[]
    hit_reasons?: Array<{
      rank: number
      file_name?: string | null
      chapter_path?: string | null
      highlight_terms?: string[]
      score?: number | null
    }>
  }
  results: DocumentSearchResultItem[]
  total: number
  elapsed_ms: number
  searched_at?: string
}

export interface DocumentSearchAnswerComplete {
  query_id?: string
  query?: string
  rewritten_query?: string | null
  answer: string
  answer_mode: string
  answer_evidence: NonNullable<DocumentSearchResponse['answer_evidence']>
  citation_validation: NonNullable<DocumentSearchResponse['citation_validation']>
}

export interface DocumentSearchStreamCallbacks {
  onSearchResult?: (result: DocumentSearchResponse) => void
  onAnswerStart?: () => void
  onAnswerDelta?: (delta: string) => void
  onAnswerComplete?: (result: DocumentSearchAnswerComplete) => void
  onError?: (message: string) => void
  onDone?: (ok: boolean) => void
}

export interface GitDocsAgentCitation {
  evidence_id: string
  resource_id: string
  chunk_id: string
  file_path: string
  heading_path: string
  commit_sha: string
  content_hash: string
  source_content?: string
  document_url?: string
}

export interface GitDocsAgentToolEvent {
  sequence: number
  tool: string
  status?: 'succeeded' | 'failed' | 'rejected'
  summary?: { candidate_count?: number }
  error?: string
}

export interface GitDocsAgentStreamCallbacks {
  onRunStarted?: (runId: string) => void
  onToolCall?: (event: GitDocsAgentToolEvent) => void
  onToolResult?: (event: GitDocsAgentToolEvent) => void
  onAnswerDelta?: (delta: string) => void
  onCitations?: (citations: GitDocsAgentCitation[]) => void
  onDone?: (status: string, toolCalls: number) => void
  onError?: (message: string) => void
}

export interface DocumentSearchDebugItem {
  rank: number
  id?: string
  type?: string
  file_name?: string | null
  file_path?: string | null
  chapter_path?: string | null
  semantic_score?: number | null
  keyword_score?: number | null
  hybrid_score?: number | null
  final_score?: number | null
  highlight_terms?: string[]
  reason?: string
}

// ========================= 工具函数 =========================
/**
 * 验证字符串是否为有效的 UUID 格式
 */
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

function parseSseBlock(block: string): { event: string; data: unknown } | null {
  const lines = block.split('\n')
  const eventLine = lines.find((line) => line.startsWith('event:'))
  const dataLines = lines.filter((line) => line.startsWith('data:'))
  if (!eventLine || dataLines.length === 0) return null
  const event = eventLine.slice('event:'.length).trim()
  const dataText = dataLines.map((line) => line.slice('data:'.length).trimStart()).join('\n')
  try {
    return { event, data: JSON.parse(dataText) }
  } catch (error) {
    logger.warn('[DocumentSearch] SSE JSON 解析失败', error)
    return null
  }
}

function buildDocumentSearchPayload(
  query: string,
  options?: {
    source?: 'knowledge' | 'git_docs'
      projectId?: string
    searchModes?: ('semantic' | 'keyword' | 'hybrid')[]
    enableRerank?: boolean
    scoreThreshold?: number
    scoreThresholdEnabled?: boolean
    topK?: number
    enableAnswer?: boolean
    enableQueryUnderstanding?: boolean
    resourceIds?: string[]
  } & DifyRetrievalOptions
): Record<string, unknown> {
  return {
    query,
    source: options?.source ?? 'knowledge',
    resource_ids: options?.resourceIds,
    retrieval_method: options?.retrievalMethod,
    reranking_mode: options?.rerankingMode,
    weights: options?.weights,
    search_modes: options?.searchModes || ['semantic'],
    enable_rerank: options?.enableRerank ?? false,
    enable_answer: options?.enableAnswer ?? false,
    enable_query_understanding: options?.enableQueryUnderstanding ?? true,
    score_threshold: options?.scoreThreshold,
    score_threshold_enabled: options?.scoreThresholdEnabled,
    top_k: options?.topK ?? 10
  }
}

interface GroupResponse {
  id: string
  tenant_id: string
  name: string
  description?: string
  sort_order: number
  is_collapsed: boolean
  knowledge_base_count: number
  created_at: string
  updated_at: string
}

interface TagResponse {
  id: string
  tenant_id: string
  name: string
  color: string
  usage_count: number
  created_at: string
}

interface KnowledgeBaseResponse {
  id: string
  tenant_id: string
  group_id?: string
  group?: { id: string; name: string }
  embedding_model_id?: string | null
  embedding_dimension?: number
  rerank_model_id?: string | null
  indexing_technique?: KnowledgeIndexingTechnique
  retrieval_model?: KnowledgeRetrievalModel
  name: string
  description?: string
  type: KnowledgeBaseType
  status: 'active' | 'inactive' | 'processing'
  document_count: number
  image_count?: number
  video_count?: number
  app_count: number
  is_favorited: boolean
  tags: Array<{ id: string; name: string; color: string }>
  created_by: string
  created_at: string
  updated_at: string
}

interface GroupWithKnowledgeBases {
  id: string
  name: string
  is_collapsed: boolean
  knowledge_base_count: number
  knowledge_bases: KnowledgeBaseResponse[]
}

interface KnowledgeBaseListResponse {
  groups: GroupWithKnowledgeBases[]
  ungrouped: KnowledgeBaseResponse[]
  total: number
}

// QA 问答项
export interface QAItem {
  id: string
  knowledge_id: string
  resource_id?: string
  category?: string
  enabled: boolean
  sync_status: 'pending' | 'synced' | 'failed'
  questions: Array<{
    id: string
    content: string
    is_primary: boolean
    position: number
  }>
  answers: Array<{
    id: string
    content: string
    is_primary: boolean
    position: number
  }>
  primary_question?: string
  primary_answer?: string
  keywords?: string[]  // 关键词（列表查询 detail=true 或搜索时返回）
  created_by?: string
  created_at: string
  updated_at: string
}

// 资源标签
export interface ResourceTagItem {
  id: string
  name: string
  color: string
  description?: string
  usage_count?: number
  created_at?: string
  /** 关联的知识库 ID */
  knowledge_id?: string
}

// 数据来源类型
export type SourceType = 'upload' | 'docs_server' | 'api'

// 资源项（knowledge_resources 表）
export interface ResourceItem {
  id: string
  resource_type: 'document' | 'image' | 'video' | 'qa_import'
  content_structure: 'text' | 'qa' | 'image' | 'video' | 'mixed'
  processing_status: 'pending' | 'processing' | 'completed' | 'failed'  // 处理状态
  file_name?: string
  file_size?: number
  content_count: number  // 内容数量（文本块/图片/切片数）
  attachment_count: number  // 附件数量
  status: 'active' | 'inactive'  // 启用状态
  // 数据来源
  source_type: SourceType  // 数据来源: upload/docs_server/api
  external_id?: string  // 外部系统ID
  source_updated_at?: string  // 外部系统更新时间
  sync_locked: boolean  // 是否锁定同步
  // 审计
  created_by?: string  // 创建人/更新人
  created_at: string
  updated_at: string
  tags?: ResourceTagItem[]  // 标签列表
}

// ========================= 数据转换 =========================

/**
 * 转换后端分组响应为前端格式
 */
function convertGroup(data: GroupResponse): KnowledgeBaseGroup {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    knowledgeBaseCount: data.knowledge_base_count,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  }
}

/**
 * 转换后端知识库响应为前端格式
 */
function convertKnowledgeBase(data: KnowledgeBaseResponse): KnowledgeBase {
  const embeddingModelId = data.embedding_model_id || data.retrieval_model?.embedding_model?.id

  return {
    id: data.id,
    name: data.name,
    description: data.description,
    type: data.type,
    status: data.status,
    documentCount: data.document_count,
    imageCount: data.image_count,
    videoCount: data.video_count,
    appRefCount: data.app_count,
    isFavorite: data.is_favorited,
    tags: data.tags?.map(t => t.name) || [],
    groupId: data.group_id,
    embeddingModelId,
    embeddingDimension: data.embedding_dimension,
    rerankModelId: data.rerank_model_id || undefined,
    indexingTechnique: data.indexing_technique,
    retrievalModel: data.retrieval_model,
    createdBy: data.created_by,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  }
}

// ========================= 分组 API =========================
export const groupsApi = {
  /**
   * 获取分组列表
   */
  async list(): Promise<KnowledgeBaseGroup[]> {
    const data = await apiClient.get<{ items: GroupResponse[]; total: number }>(
      `${BASE_PATH}/groups`
    )
    return data.items?.map(convertGroup) || []
  },

  /**
   * 创建分组
   */
  async create(data: { name: string; description?: string }): Promise<KnowledgeBaseGroup> {
    const response = await apiClient.post<GroupResponse>(
      `${BASE_PATH}/groups`,
      data
    )
    return convertGroup(response)
  },

  /**
   * 更新分组
   */
  async update(id: string, data: { name?: string; description?: string }): Promise<KnowledgeBaseGroup> {
    const response = await apiClient.put<GroupResponse>(
      `${BASE_PATH}/groups/${id}`,
      data
    )
    return convertGroup(response)
  },

  /**
   * 删除分组
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/groups/${id}`)
  }
}

// ========================= 标签 API =========================
export const tagsApi = {
  /**
   * 获取标签列表
   */
  async list(): Promise<TagResponse[]> {
    const data = await apiClient.get<{ items: TagResponse[]; total: number }>(
      `${BASE_PATH}/tags`
    )
    return data.items || []
  },

  /**
   * 创建标签
   */
  async create(data: { name: string; color?: string }): Promise<TagResponse> {
    const response = await apiClient.post<TagResponse>(
      `${BASE_PATH}/tags`,
      data
    )
    return response
  },

  /**
   * 删除标签
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/tags/${id}`)
  }
}

// ========================= 知识库 API =========================
export const knowledgeBasesApi = {
  /**
   * 获取知识库列表 (按分组组织)
   */
  async list(params?: {
    keyword?: string
    group_id?: string
    type?: KnowledgeBaseType
    status?: string
    is_favorited?: boolean
  }): Promise<{ knowledgeBases: KnowledgeBase[]; groups: KnowledgeBaseGroup[] }> {
    const data = await apiClient.get<KnowledgeBaseListResponse>(
      `${BASE_PATH}/knowledge-bases`,
      { params }
    )

    // 安全检查：确保数据存在
    if (!data || !data.groups) {
      logger.warn('[知识库API] 返回数据格式异常:', data)
      return { knowledgeBases: [], groups: [] }
    }

    // 转换分组
    const groups = data.groups.map(g => ({
      id: g.id,
      name: g.name,
      knowledgeBaseCount: g.knowledge_base_count,
      createdAt: '',
      updatedAt: ''
    } as KnowledgeBaseGroup))

    // 合并所有知识库
    const allKnowledgeBases: KnowledgeBase[] = []

    // 从分组中提取
    data.groups.forEach(group => {
      group.knowledge_bases.forEach(kb => {
        allKnowledgeBases.push(convertKnowledgeBase(kb))
      })
    })

    // 添加未分组的
    if (data.ungrouped) {
      data.ungrouped.forEach(kb => {
        allKnowledgeBases.push(convertKnowledgeBase(kb))
      })
    }

    return { knowledgeBases: allKnowledgeBases, groups }
  },

  /**
   * 获取知识库详情
   */
  async getById(id: string): Promise<KnowledgeBase> {
    const response = await apiClient.get<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}`
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 创建知识库
   */
  async create(data: CreateKnowledgeBaseForm & { groupId?: string }): Promise<KnowledgeBase> {
    // 构建请求体，只包含有值的字段
    const requestBody: Record<string, unknown> = {
      name: data.name,
      type: data.type
    }

    // 可选字段：只在有值时才添加（避免发送空字符串或 undefined）
    if (data.description && data.description.trim()) {
      requestBody.description = data.description.trim()
    }
    // group_id 必须是有效的 UUID 格式才发送（排除"未分组"等非 UUID 字符串）
    if (data.groupId && isValidUUID(data.groupId)) {
      requestBody.group_id = data.groupId
    }
    // embedding_model_id 必须是有效的 UUID 格式才发送
    if (data.settings?.embeddingModelId && isValidUUID(data.settings.embeddingModelId)) {
      requestBody.embedding_model_id = data.settings.embeddingModelId
    }
    if (data.settings?.rerankModelId && isValidUUID(data.settings.rerankModelId)) {
      requestBody.rerank_model_id = data.settings.rerankModelId
    }
    if (data.settings?.indexingTechnique) {
      requestBody.indexing_technique = data.settings.indexingTechnique
    }
    if (data.settings?.retrievalModel) {
      requestBody.retrieval_model = data.settings.retrievalModel
    }

    const response = await apiClient.post<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases`,
      requestBody
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 更新知识库
   */
  async update(id: string, data: {
    name?: string
    description?: string
    status?: string
    group_id?: string | null  // null 表示移到未分组
    embedding_model_id?: string | null
    rerank_model_id?: string | null
    indexing_technique?: KnowledgeIndexingTechnique
    retrieval_model?: KnowledgeRetrievalModel
  }): Promise<KnowledgeBase> {
    // 构建请求体，只包含有值的字段
    const requestBody: Record<string, unknown> = {}

    if (data.name !== undefined) {
      requestBody.name = data.name
    }
    if (data.description !== undefined) {
      requestBody.description = data.description
    }
    if (data.status !== undefined) {
      requestBody.status = data.status
    }
    if (data.embedding_model_id !== undefined) {
      requestBody.embedding_model_id = data.embedding_model_id || null
    }
    if (data.rerank_model_id !== undefined) {
      requestBody.rerank_model_id = data.rerank_model_id || null
    }
    if (data.indexing_technique !== undefined) {
      requestBody.indexing_technique = data.indexing_technique
    }
    if (data.retrieval_model !== undefined) {
      requestBody.retrieval_model = data.retrieval_model
    }
    // group_id: 如果传入有效 UUID 则更新，传入 null 则设为未分组
    if (data.group_id !== undefined) {
      if (data.group_id === null || data.group_id === '') {
        requestBody.group_id = null  // 移到未分组
      } else if (isValidUUID(data.group_id)) {
        requestBody.group_id = data.group_id
      }
    }

    const response = await apiClient.put<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}`,
      requestBody
    )
    return convertKnowledgeBase(response)
  },

  async uploadCreateFile(file: File): Promise<{
    oss_key: string
    oss_url: string
    filename: string
    size: number
  }> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post<{
      oss_key: string
      oss_url: string
      filename: string
      size: number
    }>(
      `${BASE_PATH}/knowledge-bases/upload-temp`,
      formData,
      { headers: { 'Content-Type': null } }
    )
  },

  async createWithDocuments(data: {
    knowledgeBase: CreateKnowledgeBaseForm & { groupId?: string }
    files: Array<{ file_url: string; file_name: string; file_size: number }>
    indexingTechnique: KnowledgeIndexingTechnique
    processRule: KnowledgeProcessRule
    retrievalModel: KnowledgeRetrievalModel
    embeddingModelId?: string
    /** 知识库 embedding 向量维度（后端按此决定 Milvus collection 后缀） */
    embeddingDimension?: number
    rerankModelId?: string
    generateCaption?: boolean
    category?: string
  }): Promise<{
    knowledgeBase: KnowledgeBase
    successCount: number
    failCount: number
    totalTexts: number
    totalAttachments: number
    resourceIds: string[]
    errors: string[]
  }> {
    // 后端 schema 已放宽到 Optional[str]：同时接受 UUID 和 ``provider::model`` 运行时引用，
    // 落 ORM 前 router 会做类型分流。这里不再用 isValidUUID 拦截，否则 service-model-X
    // 形态的运行时模型会被吞掉，导致后端走默认 doubao。
    // ``knowledge_base`` 嵌套字段仍是 ``KnowledgeBaseCreate``（UUID 类型），保留 UUID 拦截避免 Pydantic 422。
    const embeddingModelIsUuid = data.embeddingModelId ? isValidUUID(data.embeddingModelId) : false
    const rerankModelIsUuid = data.rerankModelId ? isValidUUID(data.rerankModelId) : false
    const requestBody = {
      knowledge_base: {
        name: data.knowledgeBase.name,
        description: data.knowledgeBase.description,
        type: data.knowledgeBase.type,
        group_id: data.knowledgeBase.groupId,
        embedding_model_id: embeddingModelIsUuid ? data.embeddingModelId : undefined,
        embedding_dimension: data.embeddingDimension,
        rerank_model_id: rerankModelIsUuid ? data.rerankModelId : undefined,
        indexing_technique: data.indexingTechnique,
        retrieval_model: data.retrievalModel
      },
      files: data.files,
      indexing_technique: data.indexingTechnique,
      process_rule: data.processRule,
      retrieval_model: data.retrievalModel,
      embedding_model_id: data.embeddingModelId,
      embedding_dimension: data.embeddingDimension,
      rerank_model_id: data.rerankModelId,
      generate_caption: data.generateCaption ?? true,
      category: data.category
    }
    const response = await apiClient.post<{
      knowledge_base: KnowledgeBaseResponse
      success_count: number
      fail_count: number
      total_texts: number
      total_attachments: number
      resource_ids: string[]
      errors: string[]
    }>(`${BASE_PATH}/knowledge-bases/create-with-documents`, requestBody)
    return {
      knowledgeBase: convertKnowledgeBase(response.knowledge_base),
      successCount: response.success_count,
      failCount: response.fail_count,
      totalTexts: response.total_texts,
      totalAttachments: response.total_attachments,
      resourceIds: response.resource_ids,
      errors: response.errors
    }
  },

  /**
   * 删除知识库
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/knowledge-bases/${id}`)
  },

  /**
   * 移动知识库到分组
   */
  async moveToGroup(id: string, groupId: string | null): Promise<KnowledgeBase> {
    const response = await apiClient.post<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}/move`,
      { group_id: groupId }
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 复制知识库
   */
  async copy(id: string, data?: { name?: string; groupId?: string }): Promise<KnowledgeBase> {
    const response = await apiClient.post<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}/copy`,
      { name: data?.name, group_id: data?.groupId }
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 切换收藏状态
   */
  async toggleFavorite(id: string): Promise<KnowledgeBase> {
    const response = await apiClient.post<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}/favorite`
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 添加标签
   */
  async addTags(id: string, tagIds: string[]): Promise<KnowledgeBase> {
    const response = await apiClient.post<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}/tags`,
      { tag_ids: tagIds }
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 移除标签
   */
  async removeTags(id: string, tagIds: string[]): Promise<KnowledgeBase> {
    const response = await apiClient.delete<KnowledgeBaseResponse>(
      `${BASE_PATH}/knowledge-bases/${id}/tags`,
      { data: { tag_ids: tagIds } }
    )
    return convertKnowledgeBase(response)
  },

  /**
   * 上传文件到知识库
   * 文件会上传到 OSS，路径格式: knowledge/files/[知识库类型]/[知识库名称]/[日期YYYY-MM-DD]/[文件名称]
   */
  async uploadFile(knowledgeBaseId: string, file: File): Promise<{
    oss_key: string
    oss_url: string
    filename: string
    size: number
  }> {
    const formData = new FormData()
    formData.append('file', file)

    // axios 实例默认 'Content-Type: application/json'（见 api/index.ts）。
    // 对 FormData，axios 1.x 不会自动覆盖默认 Content-Type —— 如果留默认值，
    // 浏览器发出的请求会带 application/json 头 + FormData body，FastAPI 无法解析 → 422。
    // 手写 'multipart/form-data' 又会丢 boundary，同样 422。
    // 正解：把 Content-Type 置为 null，让 axios 的 XHR/fetch 适配层重新计算，
    // 浏览器会自动生成 'multipart/form-data; boundary=----xxx'。
    const response = await apiClient.post<{
      oss_key: string
      oss_url: string
      filename: string
      size: number
    }>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/upload`,
      formData,
      { headers: { 'Content-Type': null } },
    )
    return response
  },

  /**
   * 上传图片/视频到知识库（用于问答中的富文本）
   * 文件会上传到 OSS，路径格式: knowledge/files/[知识库类型]/[知识库名称]/upload_file/[文件名称]
   */
  async uploadMedia(knowledgeBaseId: string, file: File): Promise<{
    oss_key: string
    oss_url: string
    filename: string
    size: number
    type: 'image' | 'video'
  }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.post<{
      oss_key: string
      oss_url: string
      filename: string
      size: number
      type: 'image' | 'video'
    }>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/upload-media`,
      formData,
      { headers: { 'Content-Type': null } },
    )
    return response
  },

  /**
   * 上传近义词文件到知识库
   * 文件会上传到 OSS，路径格式: knowledge/files/[知识库类型]/[知识库名称]/近义词/[日期YYYY-MM-DD]/[文件名称]
   */
  async uploadSynonymFile(knowledgeBaseId: string, file: File): Promise<{
    oss_key: string
    oss_url: string
    filename: string
    size: number
  }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.post<{
      oss_key: string
      oss_url: string
      filename: string
      size: number
    }>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/upload-synonym`,
      formData,
      { headers: { 'Content-Type': null } },
    )
    return response
  },

  /**
   * 导入 QA 问答文档
   * @param knowledgeBaseId 知识库ID
   * @param knowledgeBase 知识库结构信息
   * @param files 文件列表
   */
  async importQA(
    knowledgeBaseId: string,
    knowledgeBase: {
      id: string
      name: string
      type: number
      description?: string
      group_id?: string
      group_name?: string
      embedding_model_id?: string
    },
    files: {
      file_name: string
      file_size: number
      file_url: string
    }[],
    questionConfig?: {
      segmentationType: 'smart' | 'custom'
      separator?: string
    }
  ): Promise<{
    task_id: string
    status: string
    total_files: number
    message: string
  }> {
    logger.info('============================================================')
    logger.info('[前端] 导入 QA 问答请求参数:')
    logger.info('[前端] 知识库ID:', knowledgeBaseId)
    logger.info('[前端] 知识库结构信息:', JSON.stringify(knowledgeBase, null, 2))
    logger.info('[前端] 文件列表:', JSON.stringify(files, null, 2))
    logger.info('============================================================')

    // 后端路径: /api/v1/kb/qa/import
    const response = await apiClient.post<{
      task_id: string
      status: string
      total_files: number
      message: string
    }>(
      `${BASE_PATH}/qa/import`,
      {
        knowledge_base: knowledgeBase,
        files,
        question_segmentation_type: questionConfig?.segmentationType ?? 'smart',
        question_separator: questionConfig?.segmentationType === 'custom' ? questionConfig.separator ?? '' : undefined
      }
    )
    return response
  },

  // ========================= 文档上传 API =========================

  /**
   * 批量上传文档
   * @param knowledgeBaseId 知识库ID
   * @param knowledgeBase 知识库信息
   * @param files 文件列表（已上传到 OSS）
   * @param processConfig 处理配置
   */
  async uploadDocuments(
    knowledgeBaseId: string,
    knowledgeBase: {
      id: string
      name: string
      type: number
      description?: string
      group_id?: string
      group_name?: string
      embedding_model_id?: string
    },
    files: {
      file_url: string
      file_name: string
      file_size: number
    }[],
    processConfig?: {
      mode?: 'automatic' | 'custom'
      segmentation?: {
        method?: string
        separator?: string
        max_tokens?: number
        chunk_overlap?: number
      }
    },
    options?: {
      generateCaption?: boolean
      multimodalModelId?: string
      category?: string
    }
  ): Promise<{
    success_count: number
    fail_count: number
    total_texts: number
    total_attachments: number
    errors: string[]
    resource_ids: string[]
  }> {
    logger.info('============================================================')
    logger.info('[前端] 批量上传文档请求参数:')
    logger.info('[前端] 知识库ID:', knowledgeBaseId)
    logger.info('[前端] 知识库信息:', JSON.stringify(knowledgeBase, null, 2))
    logger.info('[前端] 文件列表:', JSON.stringify(files, null, 2))
    logger.info('[前端] 处理配置:', JSON.stringify(processConfig, null, 2))
    logger.info('============================================================')

    // 后端路径: /api/v1/kb/documents/batch
    const response = await apiClient.post<{
      success_count: number
      fail_count: number
      total_texts: number
      total_attachments: number
      errors: string[]
      resource_ids: string[]
    }>(
      `${BASE_PATH}/documents/batch`,
      {
        knowledge_base: knowledgeBase,
        files,
        process_config: processConfig || { mode: 'automatic' },
        embedding_model_id: knowledgeBase.embedding_model_id,
        generate_caption: options?.generateCaption ?? false,
        multimodal_model_id: options?.multimodalModelId,
        category: options?.category
      }
    )
    return response
  },

  /**
   * 获取文档文本列表
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async getDocumentTexts(
    knowledgeBaseId: string,
    params?: {
      resourceId?: string
      keyword?: string
      enabled?: boolean
      contentType?: 'text' | 'image' | ''  // 内容类型筛选：text=纯文本，image=有图片
      withAttachments?: boolean
      page?: number
      pageSize?: number
    }
  ): Promise<{
    items: Array<{
      id: string
      resource_id: string
      name: string
      content: string
      content_preview: string
      word_count: number
      keywords: string[]
      enabled: boolean
      sync_status: string
      attachment_count: number
      created_at: string
      updated_at: string
    }>
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: Array<{
        id: string
        resource_id: string
        name: string
        content: string
        content_preview: string
        word_count: number
        keywords: string[]
        enabled: boolean
        sync_status: string
        attachment_count: number
        created_at: string
        updated_at: string
      }>
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/documents`,
      {
        params: {
          resource_id: params?.resourceId,
          keyword: params?.keyword,
          enabled: params?.enabled,
          content_type: params?.contentType || undefined,  // 内容类型筛选
          with_attachments: params?.withAttachments ?? false,
          page: params?.page || 1,
          page_size: params?.pageSize || 20
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 文档搜索（命中测试）
   * @param knowledgeBaseId 知识库ID
   * @param query 搜索查询
   * @param options 搜索选项
   */
  async searchDocuments(
    knowledgeBaseId: string,
    query: string,
    options?: {
      source?: 'knowledge' | 'git_docs'
      projectId?: string
      // 兼容老入参（**单选**，多选会被后端 422 拒绝）
      searchModes?: ('semantic' | 'keyword' | 'hybrid')[]
      enableRerank?: boolean
      scoreThreshold?: number
      // Dify ``score_threshold_enabled`` 三态；undefined → 后端按 KB 默认决定
      scoreThresholdEnabled?: boolean
      topK?: number
      enableAnswer?: boolean
      enableQueryUnderstanding?: boolean
      resourceIds?: string[]
    } & DifyRetrievalOptions
  ): Promise<DocumentSearchResponse> {
    const response = await apiClient.post<DocumentSearchResponse>(
      `${BASE_PATH}/documents/search`,
      buildDocumentSearchPayload(query, options),
      {
        headers: {
          ...(options?.projectId ? { 'X-Project-Id': options.projectId } : {}),
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  async streamSearchDocuments(
    knowledgeBaseId: string,
    query: string,
    options: {
      source?: 'knowledge' | 'git_docs'
      projectId?: string
      searchModes?: ('semantic' | 'keyword' | 'hybrid')[]
      enableRerank?: boolean
      scoreThreshold?: number
      scoreThresholdEnabled?: boolean
      topK?: number
      enableAnswer?: boolean
      enableQueryUnderstanding?: boolean
      resourceIds?: string[]
    } & DifyRetrievalOptions,
    callbacks: DocumentSearchStreamCallbacks
  ): Promise<void> {
    const baseURL = String(apiClient.defaults.baseURL || '/api/v1').replace(/\/$/, '')
    const token = tokenHolder.getAccessToken()
    const response = await fetch(`${baseURL}${BASE_PATH}/documents/search/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Project-Id': options.projectId || getProjectId(),
        'X-User-Id': getUserId(),
        'X-Knowledge-Id': knowledgeBaseId,
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(buildDocumentSearchPayload(query, options))
    })
    if (!response.ok || !response.body) {
      throw new Error(`流式检索请求失败：${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() ?? ''
      for (const block of blocks) {
        const parsed = parseSseBlock(block.trim())
        if (!parsed) continue
        if (parsed.event === 'search_result') {
          callbacks.onSearchResult?.(parsed.data as DocumentSearchResponse)
        } else if (parsed.event === 'answer_start') {
          callbacks.onAnswerStart?.()
        } else if (parsed.event === 'answer_delta') {
          const data = parsed.data as { delta?: string }
          if (data.delta) callbacks.onAnswerDelta?.(data.delta)
        } else if (parsed.event === 'answer_complete') {
          callbacks.onAnswerComplete?.(parsed.data as DocumentSearchAnswerComplete)
        } else if (parsed.event === 'error') {
          const data = parsed.data as { message?: string }
          callbacks.onError?.(data.message || '流式检索失败')
        } else if (parsed.event === 'done') {
          const data = parsed.data as { ok?: boolean }
          callbacks.onDone?.(data.ok ?? true)
        }
      }
    }
  },

  /**
   * 从文档源增量更新当前知识库。
   *
   * 当前后端复用 docs_server 同步链路；docs_server 由 GitLab 文档同步入库后，
   * 点击该按钮即可把最新文档重新切分、向量化并刷新检索数据库。
   */
  async syncDocumentsFromGitLab(
    knowledgeBaseId: string,
    options?: { force?: boolean; includeDisabled?: boolean }
  ): Promise<KnowledgeSyncResult> {
    const response = await apiClient.post<KnowledgeSyncResult>(
      `${BASE_PATH}/sync/docs-server`,
      {
        force: options?.force ?? false,
        include_disabled: options?.includeDisabled ?? false
      },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  async getGitDocsVersionStatus(
    knowledgeBaseId: string,
    options?: { projectId?: string },
  ): Promise<GitDocsVersionStatus> {
    const response = await apiClient.get<GitDocsVersionStatus>(
      `${BASE_PATH}/git-docs/status`,
      {
        headers: {
          ...(options?.projectId ? { 'X-Project-Id': options.projectId } : {}),
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  async queryGitDocs(
    options: {
      query: string
      topK?: number
      enableAnswer?: boolean
      enableQueryUnderstanding?: boolean
      projectId?: string
      knowledgeBaseId?: string
    }
  ): Promise<DocumentSearchResponse> {
    const response = await apiClient.post<DocumentSearchResponse>(
      `${BASE_PATH}/git-docs/query`,
      {
        query: options.query,
        top_k: options.topK ?? 5,
        enable_answer: options.enableAnswer ?? false,
        enable_query_understanding: options.enableQueryUnderstanding ?? true
      },
      {
        headers: {
          ...(options.projectId ? { 'X-Project-Id': options.projectId } : {}),
          ...(options.knowledgeBaseId ? { 'X-Knowledge-Id': options.knowledgeBaseId } : {})
        }
      }
    )
    return response
  },

  async answerGitDocsQuery(
    queryId: string,
    options?: { projectId?: string; knowledgeBaseId?: string }
  ): Promise<DocumentSearchAnswerComplete> {
    const response = await apiClient.post<DocumentSearchAnswerComplete>(
      `${BASE_PATH}/git-docs/answer`,
      { query_id: queryId },
      {
        headers: {
          ...(options?.projectId ? { 'X-Project-Id': options.projectId } : {}),
          ...(options?.knowledgeBaseId ? { 'X-Knowledge-Id': options.knowledgeBaseId } : {})
        }
      }
    )
    return response
  },

  async streamAnswerGitDocsQuery(
    queryId: string,
    options: { projectId?: string; knowledgeBaseId?: string },
    callbacks: Omit<DocumentSearchStreamCallbacks, 'onSearchResult'>
  ): Promise<void> {
    const baseURL = String(apiClient.defaults.baseURL || '/api/v1').replace(/\/$/, '')
    const token = tokenHolder.getAccessToken()
    const response = await fetch(`${baseURL}${BASE_PATH}/git-docs/answer/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.projectId ? { 'X-Project-Id': options.projectId } : {}),
        ...(options.knowledgeBaseId ? { 'X-Knowledge-Id': options.knowledgeBaseId } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ query_id: queryId })
    })
    if (!response.ok || !response.body) {
      throw new Error(`流式智能总结请求失败：${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() ?? ''
      for (const block of blocks) {
        const parsed = parseSseBlock(block.trim())
        if (!parsed) continue
        if (parsed.event === 'answer_start') {
          callbacks.onAnswerStart?.()
        } else if (parsed.event === 'answer_delta') {
          const data = parsed.data as { delta?: string }
          if (data.delta) callbacks.onAnswerDelta?.(data.delta)
        } else if (parsed.event === 'answer_complete') {
          callbacks.onAnswerComplete?.(parsed.data as DocumentSearchAnswerComplete)
        } else if (parsed.event === 'error') {
          const data = parsed.data as { message?: string }
          callbacks.onError?.(data.message || '流式智能总结失败')
        } else if (parsed.event === 'done') {
          const data = parsed.data as { ok?: boolean }
          callbacks.onDone?.(data.ok ?? true)
        }
      }
    }
  },

  async streamGitDocsAgentRun(
    options: { query: string; knowledgeBaseId: string; projectId?: string; conversationId?: string },
    callbacks: GitDocsAgentStreamCallbacks,
  ): Promise<void> {
    const baseURL = String(apiClient.defaults.baseURL || '/api/v1').replace(/\/$/, '')
    const token = tokenHolder.getAccessToken()
    const response = await fetch(`${baseURL}/agents/git-docs-retrieval/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.projectId ? { 'X-Project-Id': options.projectId } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        query: options.query,
        knowledge_id: options.knowledgeBaseId,
        conversation_id: options.conversationId,
        response_mode: 'stream',
        include_debug: false,
      }),
    })
    if (!response.ok || !response.body) {
      throw new Error(`Agent 检索请求失败：${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() ?? ''
      for (const block of blocks) {
        const parsed = parseSseBlock(block.trim())
        if (!parsed) continue
        if (parsed.event === 'run_started') {
          const data = parsed.data as { run_id?: string }
          if (data.run_id) callbacks.onRunStarted?.(data.run_id)
        } else if (parsed.event === 'tool_call') {
          callbacks.onToolCall?.(parsed.data as GitDocsAgentToolEvent)
        } else if (parsed.event === 'tool_result') {
          callbacks.onToolResult?.(parsed.data as GitDocsAgentToolEvent)
        } else if (parsed.event === 'answer_delta') {
          const data = parsed.data as { delta?: string }
          if (data.delta) callbacks.onAnswerDelta?.(data.delta)
        } else if (parsed.event === 'citations') {
          const data = parsed.data as { items?: GitDocsAgentCitation[] }
          callbacks.onCitations?.(data.items ?? [])
        } else if (parsed.event === 'done') {
          const data = parsed.data as { status?: string; tool_calls?: number }
          callbacks.onDone?.(data.status ?? 'completed', data.tool_calls ?? 0)
        } else if (parsed.event === 'error') {
          const data = parsed.data as { message?: string }
          callbacks.onError?.(data.message || 'Agent 检索失败')
        }
      }
    }
  },

  async startGitDocsSync(
    knowledgeBaseId: string,
    options?: { force?: boolean; enableLlmSummary?: boolean; projectId?: string }
  ): Promise<GitDocsSyncTaskStatus> {
    const response = await apiClient.post<GitDocsSyncTaskStatus>(
      `${BASE_PATH}/git-docs/sync`,
      {
        force: options?.force ?? false,
        enable_llm_summary: options?.enableLlmSummary ?? true
      },
      {
        headers: {
          ...(options?.projectId ? { 'X-Project-Id': options.projectId } : {}),
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  async getGitDocsSyncTask(
    taskId: string,
    options: { knowledgeBaseId: string; projectId?: string }
  ): Promise<GitDocsSyncTaskStatus> {
    const response = await apiClient.get<GitDocsSyncTaskStatus>(
      `${BASE_PATH}/git-docs/sync/${taskId}`,
      {
        headers: {
          ...(options.projectId ? { 'X-Project-Id': options.projectId } : {}),
          'X-Knowledge-Id': options.knowledgeBaseId
        }
      }
    )
    return response
  },

  async retryGitDocsSyncTask(
    taskId: string,
    options: { knowledgeBaseId: string; projectId?: string }
  ): Promise<GitDocsSyncTaskStatus> {
    const response = await apiClient.post<GitDocsSyncTaskStatus>(
      `${BASE_PATH}/git-docs/sync/${taskId}/retry`,
      {},
      {
        headers: {
          ...(options.projectId ? { 'X-Project-Id': options.projectId } : {}),
          'X-Knowledge-Id': options.knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 获取文档统计信息
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID（可选）
   */
  async getDocumentStats(
    knowledgeBaseId: string,
    resourceId?: string
  ): Promise<{
    total_texts: number
    enabled_texts: number
    total_attachments: number
    total_words: number
    avg_words_per_text: number
    sync_stats: {
      pending: number
      synced: number
      failed: number
    }
  }> {
    const response = await apiClient.get<{
      total_texts: number
      enabled_texts: number
      total_attachments: number
      total_words: number
      avg_words_per_text: number
      sync_stats: {
        pending: number
        synced: number
        failed: number
      }
    }>(
      `${BASE_PATH}/documents/stats`,
      {
        params: { resource_id: resourceId },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 获取文本详情（带附件）
   * @param knowledgeBaseId 知识库ID
   * @param textId 文本ID
   */
  async getDocumentTextDetail(
    knowledgeBaseId: string,
    textId: string
  ): Promise<{
    id: string
    resource_id: string
    name: string
    content: string
    word_count: number
    keywords: string[]
    enabled: boolean
    sync_status: string
    position: number
    created_at: string
    updated_at: string
    attachments: Array<{
      id: string
      type: string
      url: string
      caption: string | null
      position: number
    }>
  }> {
    const response = await apiClient.get<{
      id: string
      resource_id: string
      name: string
      content: string
      word_count: number
      keywords: string[]
      enabled: boolean
      sync_status: string
      position: number
      created_at: string
      updated_at: string
      attachments: Array<{
        id: string
        type: string
        url: string
        caption: string | null
        position: number
      }>
    }>(
      `${BASE_PATH}/documents/${textId}`,
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量修改文本
   * @param knowledgeBaseId 知识库ID
   * @param items 修改项列表
   */
  async batchUpdateDocumentTexts(
    knowledgeBaseId: string,
    items: Array<{
      id: string
      name?: string
      content?: string
      keywords?: string[]
    }>
  ): Promise<{
    success_count: number
    fail_count: number
    results: Array<{
      id: string
      success: boolean
      revectorized: boolean
      error?: string
    }>
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
      results: Array<{
        id: string
        success: boolean
        revectorized: boolean
        error?: string
      }>
    }>(
      `${BASE_PATH}/documents/batch`,
      { items },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量启用/禁用文本
   * @param knowledgeBaseId 知识库ID
   * @param textIds 文本ID列表
   * @param enabled 是否启用
   */
  async batchEnableDocumentTexts(
    knowledgeBaseId: string,
    textIds: string[],
    enabled: boolean
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/documents/enable`,
      { text_ids: textIds, enabled },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量删除文本
   * @param knowledgeBaseId 知识库ID
   * @param ids 文本ID列表
   * @param hard 是否硬删除
   */
  async batchDeleteDocumentTexts(
    knowledgeBaseId: string,
    ids: string[],
    hard: boolean = false
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    return await apiClient.delete<{ success_count: number; fail_count: number }>(
      `${BASE_PATH}/documents/batch`,
      { data: { ids, hard }, headers: { 'X-Knowledge-Id': knowledgeBaseId } }
    )
  },

  /**
   * 删除资源（级联删除文本+附件+向量）
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   */
  async deleteDocumentResource(
    knowledgeBaseId: string,
    resourceId: string
  ): Promise<number> {
    const response = await apiClient.delete<number>(
      `${BASE_PATH}/documents/resource/${resourceId}`,
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 切换资源同步锁定状态
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   * @param locked 是否锁定
   */
  async toggleSyncLock(
    knowledgeBaseId: string,
    resourceId: string,
    locked: boolean
  ): Promise<{ sync_locked: boolean }> {
    const response = await apiClient.patch<{ sync_locked: boolean }>(
      `${BASE_PATH}/resources/${resourceId}/sync-lock`,
      null,
      {
        params: { locked },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量启用/禁用资源
   * @param knowledgeBaseId 知识库ID
   * @param resourceIds 资源ID列表
   * @param enabled 是否启用
   */
  async batchEnableResources(
    knowledgeBaseId: string,
    resourceIds: string[],
    enabled: boolean
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/resources/enable`,
      resourceIds,
      {
        params: { enabled },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  // ========================= 搜索历史 API =========================

  /**
   * 获取搜索历史列表
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async getSearchHistory(
    knowledgeBaseId: string,
    params?: {
      searchScope?: 'knowledge' | 'resource'
      resourceId?: string
      keyword?: string
      page?: number
      pageSize?: number
    }
  ): Promise<{
    items: Array<{
      id: string
      search_scope: 'knowledge' | 'resource'
      resource_id?: string
      query: string
      cost_ms: number
      created_by?: string
      created_at: string
    }>
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: Array<{
        id: string
        search_scope: 'knowledge' | 'resource'
        resource_id?: string
        query: string
        cost_ms: number
        created_by?: string
        created_at: string
      }>
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/search-history`,
      {
        params: {
          search_scope: params?.searchScope,
          resource_id: params?.resourceId,
          keyword: params?.keyword,
          page: params?.page || 1,
          page_size: params?.pageSize || 20
        },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  // ========================= QA 列表 API =========================

  /**
   * 获取资源列表（knowledge_resources 表）
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async getResourceList(
    knowledgeBaseId: string,
    params?: {
      page?: number
      pageSize?: number
      keyword?: string
      resourceType?: string
      processingStatus?: string
    }
  ): Promise<{
    items: ResourceItem[]
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: ResourceItem[]
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/resources`,
      {
        params: {
          page: params?.page || 1,
          page_size: params?.pageSize || 20,
          keyword: params?.keyword,
          resource_type: params?.resourceType,
          processing_status: params?.processingStatus
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 获取 QA 问答列表
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async getQAList(
    knowledgeBaseId: string,
    params?: {
      page?: number
      pageSize?: number
      keyword?: string
      enabled?: boolean
      detail?: boolean
    }
  ): Promise<{
    items: QAItem[]
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: QAItem[]
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/qa`,
      {
        params: {
          page: params?.page || 1,
          page_size: params?.pageSize || 20,
          keyword: params?.keyword,
          enabled: params?.enabled,
          detail: params?.detail ?? true  // 默认获取完整信息
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 获取指定资源下的 QA 列表
   */
  async getQAListByResource(
    knowledgeBaseId: string,
    resourceId: string,
    params?: {
      page?: number
      pageSize?: number
      detail?: boolean
    }
  ): Promise<{
    items: QAItem[]
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: QAItem[]
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/qa`,
      {
        params: {
          resource_id: resourceId,
          page: params?.page || 1,
          page_size: params?.pageSize || 20,
          detail: params?.detail ?? true
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 获取指定资源的 QA 列表（使用新接口 /qa/resource/{resource_id}）
   */
  async getQAByResource(
    knowledgeBaseId: string,
    resourceId: string,
    params?: {
      page?: number
      pageSize?: number
      keyword?: string
      enabled?: boolean
      detail?: boolean
    }
  ): Promise<{
    items: QAItem[]
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: QAItem[]
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/qa/resource/${resourceId}`,
      {
        params: {
          page: params?.page || 1,
          page_size: params?.pageSize || 20,
          keyword: params?.keyword,
          enabled: params?.enabled,
          detail: params?.detail ?? true
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 批量启用/禁用 QA
   */
  async batchEnableQA(
    knowledgeBaseId: string,
    qaIds: string[],
    enabled: boolean
  ): Promise<{ success_count: number; fail_count: number }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/qa/enable`,
      {
        qa_ids: qaIds,
        enabled: enabled
      },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 批量创建 QA（手动输入）
   */
  async batchCreateQA(
    knowledgeBaseId: string,
    embeddingModelId: string,
    items: Array<{
      name?: string
      keywords?: string[]
      questions: Array<{ content: string; is_primary: boolean; position: number }>
      answers: Array<{ content: string; is_primary: boolean; position: number }>
      enabled?: boolean
    }>,
    resourceId?: string
  ): Promise<{ success_count: number; fail_count: number; errors: string[]; success_ids: string[] }> {
    // 构建请求体，只有当 resourceId 有值时才添加
    const requestBody: Record<string, unknown> = {
      embedding_model_id: embeddingModelId,
      items
    }
    if (resourceId) {
      requestBody.resource_id = resourceId
    }

    logger.info('[API] batchCreateQA 请求:', { knowledgeBaseId, embeddingModelId, resourceId, items })

    const response = await apiClient.post<{
      success_count: number
      fail_count: number
      errors: string[]
      success_ids: string[]
    }>(
      `${BASE_PATH}/qa/batch`,
      requestBody,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 批量更新 QA
   */
  async batchUpdateQA(
    knowledgeBaseId: string,
    items: Array<{
      id: string
      name?: string
      keywords?: string[]
      questions: Array<{ content: string; is_primary: boolean; position: number }>
      answers: Array<{ content: string; is_primary: boolean; position: number }>
      enabled?: boolean
    }>
  ): Promise<{ success_count: number; fail_count: number; errors: string[] }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
      errors: string[]
    }>(
      `${BASE_PATH}/qa/batch`,
      { items },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 批量删除 QA
   */
  async batchDeleteQA(
    knowledgeBaseId: string,
    qaIds: string[],
    hard: boolean = false
  ): Promise<{ success_count: number; fail_count: number }> {
    const response = await apiClient.delete<{
      success_count: number
      fail_count: number
      errors: string[]
    }>(
      `${BASE_PATH}/qa/batch`,
      {
        data: { ids: qaIds, hard },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * QA 语义搜索（命中测试）
   * @param knowledgeBaseId 知识库ID
   * @param query 搜索查询
   * @param options 搜索选项
   */
  async searchQA(
    knowledgeBaseId: string,
    query: string,
    options?: {
      searchModes?: ('semantic' | 'keyword' | 'hybrid')[]
      enableRerank?: boolean
      scoreThreshold?: number
      scoreThresholdEnabled?: boolean
      topK?: number
      resourceIds?: string[]
      enabledOnly?: boolean
    } & DifyRetrievalOptions
  ): Promise<{
    query: string
    type: string
    search_modes: string[]
    retrieval_method?: RetrievalMethod | null
    reranking_mode?: RerankMode | null
    enable_rerank: boolean
    results: Array<{
      rank: number
      qa_id: string
      qa_name: string | null
      keywords: string[]
      matched_question_id: string
      matched_question: string
      is_primary_question: boolean
      similar_questions: Array<{
        id: string
        content: string
        position: number
      }>
      answers: Array<{
        content: string
        is_primary: boolean
        position: number
        images: string[]
        videos: string[]
        audios: string[]
        files: string[]
        weight: number
      }>
      primary_answer: string
      semantic_score: number | null
      keyword_score: number | null
      hybrid_score: number | null
      final_score: number | null
      enabled: boolean
    }>
    total: number
    elapsed_ms: number
    searched_at: string
  }> {
    const response = await apiClient.post<{
      query: string
      type: string
      search_modes: string[]
      retrieval_method?: RetrievalMethod | null
      reranking_mode?: RerankMode | null
      enable_rerank: boolean
      results: Array<{
        rank: number
        qa_id: string
        qa_name: string | null
        keywords: string[]
        matched_question_id: string
        matched_question: string
        is_primary_question: boolean
        similar_questions: Array<{
          id: string
          content: string
          position: number
        }>
        answers: Array<{
          content: string
          is_primary: boolean
          position: number
          images: string[]
          videos: string[]
          audios: string[]
          files: string[]
          weight: number
        }>
        primary_answer: string
        semantic_score: number | null
        keyword_score: number | null
        hybrid_score: number | null
        final_score: number | null
        enabled: boolean
      }>
      total: number
      elapsed_ms: number
      searched_at: string
    }>(
      `${BASE_PATH}/qa/search`,
      {
        type: 'qa',
        query,
        resource_ids: options?.resourceIds,
        // Dify 风格新字段（first-class）
        retrieval_method: options?.retrievalMethod,
        reranking_mode: options?.rerankingMode,
        weights: options?.weights,
        // 兼容老字段（仅单选）
        search_modes: options?.searchModes || ['semantic'],
        enable_rerank: options?.enableRerank ?? false,
        score_threshold: options?.scoreThreshold,
        score_threshold_enabled: options?.scoreThresholdEnabled,
        top_k: options?.topK ?? 10,
        enabled_only: options?.enabledOnly ?? true
      },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 向已有资源导入 QA
   * @param action parse-只解析 append-追加 replace-替换
   */
  async importQAToResource(
    knowledgeBaseId: string,
    resourceId: string,
    fileUrl: string,
    action: 'parse' | 'append' | 'replace',
    embeddingModelId?: string
  ): Promise<{
    status: string
    parsed_count: number
    imported_count: number
    existing_count: number
    message: string
  }> {
    const response = await apiClient.post<{
      status: string
      parsed_count: number
      imported_count: number
      existing_count: number
      message: string
    }>(
      `${BASE_PATH}/qa/resources/${resourceId}/import`,
      { file_url: fileUrl, action },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId,
          ...(embeddingModelId ? { 'X-Embedding-Model-Id': embeddingModelId } : {})
        }
      }
    )
    return response
  },

  /**
   * 获取资源详情
   */
  async getResource(
    knowledgeBaseId: string,
    resourceId: string
  ): Promise<{
    id: string
    file_name: string | null
    file_path: string | null
    file_size: number | null
    resource_type: string
    content_structure: string
    processing_status: string
    status: string
    content_count: number
    attachment_count: number
    created_by: string | null
    created_at: string
    updated_at: string
  }> {
    const result = await apiClient.get<{
      id: string
      file_name: string | null
      file_path: string | null
      file_size: number | null
      resource_type: string
      content_structure: string
      processing_status: string
      status: string
      content_count: number
      attachment_count: number
      created_by: string | null
      created_at: string
      updated_at: string
    }>(
      `${BASE_PATH}/resources/${resourceId}`,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return result
  },

  /**
   * 获取资源源文件预览 URL
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   * @param expires URL 有效期（秒），默认 1 小时
   */
  async getResourcePreviewUrl(
    knowledgeBaseId: string,
    resourceId: string,
    expires: number = 3600
  ): Promise<{
    url: string
    file_name: string | null
    expires_in: number
  }> {
    const result = await apiClient.get<{
      url: string
      file_name: string | null
      expires_in: number
    }>(
      `${BASE_PATH}/resources/${resourceId}/preview-url`,
      {
        params: { expires },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return result
  },

  /**
   * 重新分段资源
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   * @param config 分段配置
   */
  async resegmentResource(
    knowledgeBaseId: string,
    resourceId: string,
    config: {
      mode: 'automatic' | 'custom'
      separator?: string
      max_tokens?: number
      chunk_overlap?: number
    }
  ): Promise<{
    success: boolean
    text_count: number
    message: string
  }> {
    const result = await apiClient.post<{
      success: boolean
      text_count: number
      message: string
    }>(
      `${BASE_PATH}/documents/resegment/${resourceId}`,
      config,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return result
  },

  /**
   * 更新资源信息
   */
  async updateResource(
    knowledgeBaseId: string,
    resourceId: string,
    data: { file_name?: string; category?: string; status?: 'active' | 'inactive' }
  ): Promise<void> {
    await apiClient.put(
      `${BASE_PATH}/resources/${resourceId}`,
      data,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
  },

  /**
   * 更新资源状态
   */
  async updateResourceStatus(
    knowledgeBaseId: string,
    resourceId: string,
    status: 'active' | 'inactive'
  ): Promise<void> {
    await apiClient.put(
      `${BASE_PATH}/resources/${resourceId}`,
      { status },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
  },

  // ========================= 近义词 API =========================

  /**
   * 批量添加近义词
   * @param knowledgeBaseId 知识库ID
   * @param fileUrl OSS 文件URL
   * @param synonyms 近义词列表
   */
  async batchAddSynonyms(
    knowledgeBaseId: string,
    fileUrl: string | undefined,
    synonyms: { mainWord: string; synonyms: string[] }[]
  ): Promise<{ success: boolean; count: number; message: string }> {
    logger.info('============================================================')
    logger.info('[前端] 批量添加近义词请求参数:')
    logger.info('[前端] 知识库ID:', knowledgeBaseId)
    logger.info('[前端] 文件URL:', fileUrl)
    logger.info('[前端] 近义词数量:', synonyms.length)
    logger.info('============================================================')

    const response = await apiClient.post<{
      success: boolean
      count: number
      message: string
    }>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/synonyms/batch`,
      { file_url: fileUrl, synonyms }
    )
    return response
  },

  /**
   * 获取近义词列表
   * @param knowledgeBaseId 知识库ID
   * @param page 页码
   * @param pageSize 每页数量
   * @param keyword 搜索关键词（匹配实体词）
   */
  async listSynonyms(
    knowledgeBaseId: string,
    page: number = 1,
    pageSize: number = 50,
    keyword?: string
  ): Promise<{
    items: {
      id: string
      entity_word: string
      synonym_word: string[]
      created_at?: number
      updated_at?: number
    }[]
    total: number
    page: number
    page_size: number
  }> {
    const params: Record<string, string | number> = { page, page_size: pageSize }
    if (keyword && keyword.trim()) {
      params.keyword = keyword.trim()
    }
    const response = await apiClient.get<{
      items: {
        id: string
        entity_word: string
        synonym_word: string[]
        created_at?: number
        updated_at?: number
      }[]
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/synonyms`,
      { params }
    )
    return response
  },

  /**
   * 删除近义词（支持单条或批量）
   * @param knowledgeBaseId 知识库ID
   * @param synonymIds 近义词记录ID或ID数组
   */
  async deleteSynonym(knowledgeBaseId: string, synonymIds: string | string[]): Promise<{ success: boolean; message: string; deleted_count: number }> {
    const ids = Array.isArray(synonymIds) ? synonymIds : [synonymIds]
    const response = await apiClient.delete<{ success: boolean; message: string; deleted_count: number }>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/synonyms`,
      { data: { ids } }
    )
    return response
  },

  // ========================= 图片 API =========================

  /**
   * 批量上传图片
   * @param knowledgeBaseId 知识库ID
   * @param files 文件列表
   * @param options 上传选项
   */
  async batchUploadImages(
    knowledgeBaseId: string,
    files: Array<{
      file_url: string
      file_name: string
      file_size?: number
    }>,
    options?: {
      generateCaption?: boolean
      generateOcr?: boolean
      category?: string
      multimodalModelId?: string
    }
  ): Promise<{
    success_count: number
    fail_count: number
    results: Array<{
      file_name: string
      success: boolean
      image_id?: string
      resource_id?: string
      error?: string
    }>
  }> {
    // 构建 headers
    const headers: Record<string, string> = {
      'X-Knowledge-Id': knowledgeBaseId
    }
    // 如果指定了多模态模型，添加到 header
    if (options?.multimodalModelId) {
      headers['X-Multimodal-Model-Id'] = options.multimodalModelId
    }

    const response = await apiClient.post<{
      success_count: number
      fail_count: number
      results: Array<{
        file_name: string
        success: boolean
        image_id?: string
        resource_id?: string
        error?: string
      }>
    }>(
      `${BASE_PATH}/images/batch`,
      {
        knowledge_base: { id: knowledgeBaseId },
        files: files.map(f => ({
          file_url: f.file_url,
          file_name: f.file_name,
          file_size: f.file_size
        })),
        generate_caption: options?.generateCaption ?? true,
        generate_ocr: options?.generateOcr ?? true,
        category: options?.category
      },
      { headers }
    )
    return response
  },

  /**
   * 查询图片列表
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async listImages(
    knowledgeBaseId: string,
    params?: {
      resourceId?: string
      enabled?: boolean
      syncStatus?: string
      page?: number
      pageSize?: number
    }
  ): Promise<{
    items: Array<{
      id: string
      image_path: string
      file_name?: string
      file_size?: number
      caption?: string
      ocr_text?: string
      resource_id: string
      enabled: boolean
      sync_status: string
      created_by?: string
      created_at: string
    }>
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: Array<{
        id: string
        image_path: string
        file_name?: string
        file_size?: number
        caption?: string
        ocr_text?: string
        resource_id: string
        enabled: boolean
        sync_status: string
        created_by?: string
        created_at: string
      }>
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/images`,
      {
        params: {
          resource_id: params?.resourceId,
          enabled: params?.enabled,
          sync_status: params?.syncStatus,
          page: params?.page || 1,
          page_size: params?.pageSize || 20
        },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 搜索图片
   * @param knowledgeBaseId 知识库ID
   * @param query 搜索文本
   * @param options 搜索选项
   */
  async searchImages(
    knowledgeBaseId: string,
    query: string,
    options?: {
      searchModes?: string[]
      enableRerank?: boolean
      scoreThreshold?: number
      topK?: number
    }
  ): Promise<{
    query: string
    search_modes: string[]
    enable_rerank: boolean
    results: Array<{
      rank: number
      image_id: string
      resource_id: string
      file_name?: string
      image_path?: string
      caption?: string
      ocr_text?: string
      semantic_score?: number
      keyword_score?: number
      hybrid_score?: number
      final_score?: number
    }>
    total: number
    elapsed_ms: number
  }> {
    const response = await apiClient.post<{
      query: string
      search_modes: string[]
      enable_rerank: boolean
      results: Array<{
        rank: number
        image_id: string
        resource_id: string
        file_name?: string
        image_path?: string
        caption?: string
        ocr_text?: string
        semantic_score?: number
        keyword_score?: number
        hybrid_score?: number
        final_score?: number
      }>
      total: number
      elapsed_ms: number
    }>(
      `${BASE_PATH}/images/search`,
      {
        query,
        search_modes: options?.searchModes || ['semantic'],
        enable_rerank: options?.enableRerank ?? false,
        score_threshold: options?.scoreThreshold,
        top_k: options?.topK || 10
      },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 搜索视频
   * @param knowledgeBaseId 知识库ID
   * @param query 搜索文本
   * @param options 搜索选项
   */
  async searchVideos(
    knowledgeBaseId: string,
    query: string,
    options?: {
      searchModes?: string[]
      enableRerank?: boolean
      scoreThreshold?: number
      topK?: number
    }
  ): Promise<{
    query: string
    search_modes: string[]
    enable_rerank: boolean
    results: Array<{
      rank: number
      type: string           // 'video' | 'segment'
      segment_id?: string
      video_id: string
      resource_id: string
      segment_index?: number
      file_name?: string
      caption?: string
      video_path?: string
      key_frame_path?: string
      key_frame_time?: number
      semantic_score?: number
      keyword_score?: number
      hybrid_score?: number
      final_score?: number
    }>
    total: number
    elapsed_ms: number
  }> {
    const response = await apiClient.post<{
      query: string
      search_modes: string[]
      enable_rerank: boolean
      results: Array<{
        rank: number
        type: string
        segment_id?: string
        video_id: string
        resource_id: string
        segment_index?: number
        file_name?: string
        caption?: string
        key_frame_path?: string
        key_frame_time?: number
        semantic_score?: number
        keyword_score?: number
        hybrid_score?: number
        final_score?: number
      }>
      total: number
      elapsed_ms: number
    }>(
      `${BASE_PATH}/videos/search`,
      {
        query,
        search_modes: options?.searchModes || ['semantic'],
        enable_rerank: options?.enableRerank ?? false,
        score_threshold: options?.scoreThreshold,
        top_k: options?.topK || 10
      },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 更新图片 Caption
   * @param knowledgeBaseId 知识库ID
   * @param imageId 图片ID
   * @param caption 新的 Caption 内容
   */
  async updateImageCaption(
    knowledgeBaseId: string,
    imageId: string,
    caption: string
  ): Promise<void> {
    await apiClient.put<void>(
      `${BASE_PATH}/images/batch`,
      {
        items: [{
          id: imageId,
          caption: caption
        }]
      },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
  },

  /**
   * 批量启用/禁用图片
   * @param knowledgeBaseId 知识库ID
   * @param imageIds 图片ID列表
   * @param enabled 是否启用
   */
  async batchEnableImages(
    knowledgeBaseId: string,
    imageIds: string[],
    enabled: boolean
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/images/enable`,
      { ids: imageIds, enabled },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * AI 生成描述预览（不保存）
   * 用于让用户预览 AI 生成的描述，与旧描述对比后再决定是否保存
   * @param knowledgeBaseId 知识库ID
   * @param imageId 图片ID
   * @param multimodalModelId 多模态模型ID（可选）
   */
  async generateCaptionPreview(
    knowledgeBaseId: string,
    imageId: string,
    multimodalModelId?: string
  ): Promise<{ old_caption: string; new_caption: string }> {
    const headers: Record<string, string> = {
      'X-Knowledge-Id': knowledgeBaseId
    }
    if (multimodalModelId) {
      headers['X-Multimodal-Model-Id'] = multimodalModelId
    }

    const response = await apiClient.post<{ old_caption: string; new_caption: string }>(
      `${BASE_PATH}/images/${imageId}/generate-caption-preview`,
      {},
      { headers }
    )
    return response
  },

  /**
   * 删除图片资源（级联删除图片+向量）
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   */
  async deleteImageResource(
    knowledgeBaseId: string,
    resourceId: string
  ): Promise<number> {
    const response = await apiClient.delete<number>(
      `${BASE_PATH}/images/resource/${resourceId}`,
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量删除图片
   * @param knowledgeBaseId 知识库ID
   * @param imageIds 图片ID列表
   * @param hard 是否硬删除
   */
  async batchDeleteImages(
    knowledgeBaseId: string,
    imageIds: string[],
    hard: boolean = false
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    return await apiClient.delete<{ success_count: number; fail_count: number }>(
      `${BASE_PATH}/images/batch`,
      { data: { ids: imageIds, hard }, headers: { 'X-Knowledge-Id': knowledgeBaseId } }
    )
  },

  // ========================= 视频相关 API =========================

  /**
   * 批量上传视频
   * @param knowledgeBaseId 知识库ID
   * @param files 视频文件列表
   * @param options 上传选项
   */
  async batchUploadVideos(
    knowledgeBaseId: string,
    files: Array<{
      file_url: string
      file_name: string
      file_size?: number
    }>,
    options?: {
      segmentation?: {
        mode: 'automatic' | 'custom'
        interval_seconds?: number
        max_segments?: number
        timestamps?: number[]
      }
      generateCaption?: boolean
      category?: string
    }
  ): Promise<{
    success_count: number
    fail_count: number
    results: Array<{
      file_name: string
      success: boolean
      video_id?: string
      resource_id?: string
      segment_count?: number
      error?: string
    }>
  }> {
    const response = await apiClient.post<{
      success_count: number
      fail_count: number
      results: Array<{
        file_name: string
        success: boolean
        video_id?: string
        resource_id?: string
        segment_count?: number
        error?: string
      }>
    }>(
      `${BASE_PATH}/videos/batch`,
      {
        files,
        segmentation: options?.segmentation || { mode: 'automatic', interval_seconds: 1.0, max_segments: 100 },
        generate_caption: options?.generateCaption ?? true,
        category: options?.category
      },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 获取视频列表
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async listVideos(
    knowledgeBaseId: string,
    params?: {
      resourceId?: string
      enabled?: boolean
      processingStatus?: string
      page?: number
      pageSize?: number
    }
  ): Promise<{
    items: Array<{
      id: string
      video_path: string
      file_name?: string
      file_size?: number
      duration_seconds?: number
      summary?: string
      segment_count: number
      resource_id: string
      enabled: boolean
      processing_status: string
      sync_status: string
      created_by?: string
      created_at: string
    }>
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: Array<{
        id: string
        video_path: string
        file_name?: string
        file_size?: number
        duration_seconds?: number
        summary?: string
        segment_count: number
        resource_id: string
        enabled: boolean
        processing_status: string
        sync_status: string
        created_by?: string
        created_at: string
      }>
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/videos`,
      {
        params: {
          resource_id: params?.resourceId,
          enabled: params?.enabled,
          processing_status: params?.processingStatus,
          page: params?.page || 1,
          page_size: params?.pageSize || 20
        },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 获取视频详情
   * @param knowledgeBaseId 知识库ID
   * @param videoId 视频ID
   */
  async getVideoDetail(
    knowledgeBaseId: string,
    videoId: string
  ): Promise<{
    id: string
    video_path: string
    duration_seconds?: number
    summary?: string
    segment_count: number
    resource_id: string
    enabled: boolean
    processing_status: string
    processing_error?: string
    sync_status: string
    created_by?: string
    created_at: string
  }> {
    const response = await apiClient.get<{
      id: string
      video_path: string
      duration_seconds?: number
      summary?: string
      segment_count: number
      resource_id: string
      enabled: boolean
      processing_status: string
      processing_error?: string
      sync_status: string
      created_by?: string
      created_at: string
    }>(
      `${BASE_PATH}/videos/${videoId}`,
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 获取视频切片列表
   * @param knowledgeBaseId 知识库ID
   * @param videoId 视频ID
   * @param params 查询参数
   */
  async listVideoSegments(
    knowledgeBaseId: string,
    videoId: string,
    params?: {
      enabled?: boolean
      page?: number
      pageSize?: number
    }
  ): Promise<{
    items: Array<{
      id: string
      segment_index: number
      caption?: string
      key_frame_path?: string
      key_frame_time?: number
      enabled: boolean
      sync_status: string
    }>
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: Array<{
        id: string
        segment_index: number
        caption?: string
        key_frame_path?: string
        key_frame_time?: number
        enabled: boolean
        sync_status: string
      }>
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/videos/${videoId}/segments`,
      {
        params: {
          enabled: params?.enabled,
          page: params?.page || 1,
          page_size: params?.pageSize || 50
        },
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量启用/禁用视频
   * @param knowledgeBaseId 知识库ID
   * @param videoIds 视频ID列表
   * @param enabled 是否启用
   */
  async batchEnableVideos(
    knowledgeBaseId: string,
    videoIds: string[],
    enabled: boolean
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/videos/enable`,
      { ids: videoIds, enabled },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 删除视频资源（级联删除视频+切片+向量）
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   */
  async deleteVideoResource(
    knowledgeBaseId: string,
    resourceId: string
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    const response = await apiClient.delete<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/videos/resource/${resourceId}`,
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量启用/禁用视频切片
   * @param knowledgeBaseId 知识库ID
   * @param segmentIds 切片ID列表
   * @param enabled 是否启用
   */
  async batchEnableVideoSegments(
    knowledgeBaseId: string,
    segmentIds: string[],
    enabled: boolean
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
    }>(
      `${BASE_PATH}/videos/segments/enable`,
      { ids: segmentIds, enabled },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 更新视频切片描述
   * @param knowledgeBaseId 知识库ID
   * @param segmentId 切片ID
   * @param caption 描述内容
   */
  async updateVideoSegmentCaption(
    knowledgeBaseId: string,
    segmentId: string,
    caption: string
  ): Promise<{
    success_count: number
    fail_count: number
    results: Array<{
      id: string
      success: boolean
      revectorized: boolean
      error?: string
    }>
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
      results: Array<{
        id: string
        success: boolean
        revectorized: boolean
        error?: string
      }>
    }>(
      `${BASE_PATH}/videos/segments/batch`,
      { items: [{ id: segmentId, caption }] },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 更新视频摘要
   * @param knowledgeBaseId 知识库ID
   * @param videoId 视频ID
   * @param summary 摘要内容
   */
  async updateVideoSummary(
    knowledgeBaseId: string,
    videoId: string,
    summary: string
  ): Promise<{
    success_count: number
    fail_count: number
    results: Array<{
      id: string
      success: boolean
      revectorized: boolean
      error?: string
    }>
  }> {
    const response = await apiClient.put<{
      success_count: number
      fail_count: number
      results: Array<{
        id: string
        success: boolean
        revectorized: boolean
        error?: string
      }>
    }>(
      `${BASE_PATH}/videos/batch`,
      { items: [{ id: videoId, summary }] },
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 生成视频摘要预览（AI生成，不保存）
   * @param knowledgeBaseId 知识库ID
   * @param videoId 视频ID
   */
  async generateVideoSummaryPreview(
    knowledgeBaseId: string,
    videoId: string
  ): Promise<{
    old_summary: string
    new_summary: string
  }> {
    const response = await apiClient.post<{
      old_summary: string
      new_summary: string
    }>(
      `${BASE_PATH}/videos/${videoId}/generate-summary-preview`,
      {},
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 生成切片描述预览（AI生成，不保存）
   * @param knowledgeBaseId 知识库ID
   * @param segmentId 切片ID
   */
  async generateSegmentCaptionPreview(
    knowledgeBaseId: string,
    segmentId: string
  ): Promise<{
    old_caption: string
    new_caption: string
  }> {
    const response = await apiClient.post<{
      old_caption: string
      new_caption: string
    }>(
      `${BASE_PATH}/videos/segments/${segmentId}/generate-caption-preview`,
      {},
      {
        headers: { 'X-Knowledge-Id': knowledgeBaseId }
      }
    )
    return response
  },

  /**
   * 批量删除视频切片
   * @param knowledgeBaseId 知识库ID
   * @param segmentIds 切片ID列表
   * @param hard 是否硬删除
   */
  async batchDeleteVideoSegments(
    knowledgeBaseId: string,
    segmentIds: string[],
    hard: boolean = true
  ): Promise<{
    success_count: number
    fail_count: number
  }> {
    return await apiClient.delete<{ success_count: number; fail_count: number }>(
      `${BASE_PATH}/videos/segments/batch`,
      { data: { ids: segmentIds, hard }, headers: { 'X-Knowledge-Id': knowledgeBaseId } }
    )
  },

  // ========================= 附件 API =========================

  /**
   * 更新附件描述
   * @param knowledgeBaseId 知识库ID
   * @param attachmentId 附件ID
   * @param caption 新的描述内容
   */
  async updateAttachmentCaption(
    knowledgeBaseId: string,
    attachmentId: string,
    caption: string
  ): Promise<boolean> {
    const response = await apiClient.put<boolean>(
      `${BASE_PATH}/documents/attachments/${attachmentId}`,
      { caption },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 生成附件描述预览（AI生成，不保存）
   * @param knowledgeBaseId 知识库ID
   * @param attachmentId 附件ID
   * @param multimodalModelId 多模态模型ID（可选）
   */
  async generateAttachmentCaption(
    knowledgeBaseId: string,
    attachmentId: string,
    multimodalModelId?: string
  ): Promise<{ old_caption: string; new_caption: string }> {
    const headers: Record<string, string> = {
      'X-Knowledge-Id': knowledgeBaseId
    }
    if (multimodalModelId) {
      headers['X-Multimodal-Model-Id'] = multimodalModelId
    }

    const response = await apiClient.post<{ old_caption: string; new_caption: string }>(
      `${BASE_PATH}/documents/attachments/${attachmentId}/generate-caption`,
      {},
      { headers }
    )
    return response
  },

}

// ========================= 模型配置类型 =========================
export type ModelType = 'embedding' | 'llm' | 'rerank' | 'sparse' | 'multimodal'

export interface ModelConfigBrief {
  id: string
  name: string
  modelName: string
  provider?: string
  description?: string
  modelType?: ModelType
  /** 模型默认输出维度；多维可调模型为"首选维度"。null 表示运行时再决定。 */
  dimensions?: number | null
  /**
   * 模型可输出的全部维度集合（Matryoshka / dimensions 参数可调的 embedding 模型）。
   * 长度 ≥ 2 时即视为多维模型；省略 / 空数组表示"仅 `dimensions` 一种维度"。
   */
  supportedDimensions?: number[]
  isDefault?: boolean
  isVirtual?: boolean
}

interface CatalogModelResponse {
  model_id: string
  provider: string
  display_name: string
  model_type?: ModelType | 'image' | 'video'
  embedding_dimension?: number | null
  supported_dimensions?: number[] | null
  supports_vision: boolean
  tags?: string[]
  is_enabled: boolean
}

function getCatalogModelType(item: CatalogModelResponse): ModelType {
  if (item.model_type === 'embedding' || item.model_type === 'rerank' || item.model_type === 'multimodal') {
    return item.model_type
  }
  const tags = item.tags ?? []
  if (tags.includes('embedding') || isProviderModelForType(item.model_id, 'embedding')) {
    return 'embedding'
  }
  if (tags.includes('rerank') || isProviderModelForType(item.model_id, 'rerank')) {
    return 'rerank'
  }
  if (tags.includes('vision') || tags.includes('multimodal') || item.supports_vision) {
    return 'multimodal'
  }
  return 'llm'
}

function mapCatalogModelBrief(item: CatalogModelResponse): ModelConfigBrief {
  const modelType = getCatalogModelType(item)
  const supported = modelType === 'embedding'
    ? item.supported_dimensions ?? (
      item.embedding_dimension ? [item.embedding_dimension] : undefined
    )
    : undefined

  return {
    id: `${item.provider}::${item.model_id}`,
    name: item.display_name,
    modelName: item.model_id,
    provider: item.provider,
    modelType,
    dimensions: modelType === 'embedding'
      ? (item.embedding_dimension ?? null)
      : null,
    supportedDimensions: supported,
    isDefault: false,
  }
}

/**
 * 已识别的 embedding 模型元数据。
 *
 * - `defaultDimension`：模型默认输出维度（与历史 `inferEmbeddingDimension` 返回值保持兼容）。
 * - `supportedDimensions`：模型可输出的全部维度集合。
 *   - 省略 / 空数组：仅支持 `defaultDimension`（固定维度模型，如 Qwen3-Embedding 系列）。
 *   - 多元素：Matryoshka 风格或 dimensions 参数可调的模型（如 DashScope text-embedding-v3/v4）。
 *
 * key 为 `modelName.toLowerCase()`。命中后调用方可由 `supportedDimensions` 决定该模型
 * 能否落到某个 KB 维度。
 */
interface EmbeddingModelMeta {
  defaultDimension: number
  supportedDimensions?: number[]
}

const KNOWN_EMBEDDING_MODELS: Record<string, EmbeddingModelMeta> = {
  // SiliconFlow / Qwen3-Embedding 系列：固定维度，由模型规模决定。
  'qwen/qwen3-embedding-0.6b': { defaultDimension: 1024 },
  'qwen3-embedding-0.6b': { defaultDimension: 1024 },
  'qwen/qwen3-embedding-4b': { defaultDimension: 2560 },
  'qwen3-embedding-4b': { defaultDimension: 2560 },
  'qwen/qwen3-embedding-8b': { defaultDimension: 4096 },
  'qwen3-embedding-8b': { defaultDimension: 4096 },

  // 通义千问 DashScope text-embedding：dimensions 参数可调（Matryoshka）。
  // 参考阿里云模型工作室公开文档：v3 支持 1024 及以下 6 档；v4 增加 1536 / 2048。
  'text-embedding-v3': {
    defaultDimension: 1024,
    supportedDimensions: [64, 128, 256, 512, 768, 1024],
  },
  'text-embedding-v4': {
    defaultDimension: 1024,
    supportedDimensions: [64, 128, 256, 512, 768, 1024, 1536, 2048],
  },
}

function lookupEmbeddingModelMeta(modelName: string): EmbeddingModelMeta | null {
  const key = modelName.toLowerCase()
  const direct = KNOWN_EMBEDDING_MODELS[key]
  if (direct) return direct

  // Qwen3-Embedding 系列在私有服务中常带业务后缀，如
  // `Qwen3-Embedding-0.6B-Pro5000`，按规模推导固定维度。
  if (key.includes('qwen3') && key.includes('embedding')) {
    if (key.includes('0.6b')) return { defaultDimension: 1024 }
    if (key.includes('4b')) return { defaultDimension: 2560 }
    if (key.includes('8b')) return { defaultDimension: 4096 }
  }

  // 火山方舟豆包 embedding 文本系列：模型名带日期版本号（如
  // `doubao-embedding-text-240715`/`doubao-embedding-large-text-250515`/
  // `doubao-embedding-vision-251215`），统一以前缀匹配兜底，维度按 2048 固定。
  // 排除尚未发布的纯 `doubao-embedding-vision` 命名簇，避免误判。
  if (key.startsWith('doubao-embedding') && key !== 'doubao-embedding-vision') {
    return { defaultDimension: 2048 }
  }

  return null
}

function inferEmbeddingDimension(modelName: string): number | null {
  return lookupEmbeddingModelMeta(modelName)?.defaultDimension ?? null
}

/**
 * 判断某个 embedding 模型是否能用于给定的 KB 维度。
 *
 * 优先级：
 * 1. `model.supportedDimensions` 非空 → 命中数组即兼容（多维模型按维度集合判断）。
 * 2. 已知 `model.dimensions` → 必须 === 目标维度。
 * 3. 元数据缺失（`dimensions == null` 且无 `supportedDimensions`）→ 视为通用，放行。
 *
 * 抽到模块顶层导出，让 `IndexingSettingsStep`、`KnowledgeBaseCreateView` 及未来其它
 * 入口共用同一份过滤语义，避免行为分叉。
 */
export function isEmbeddingModelCompatibleWithDimension(
  model: Pick<ModelConfigBrief, 'dimensions' | 'supportedDimensions'>,
  dimension: number,
): boolean {
  const supported = model.supportedDimensions
  if (supported && supported.length > 0) {
    return supported.includes(dimension)
  }
  if (model.dimensions == null) return true
  return model.dimensions === dimension
}

function isProviderModelForType(modelName: string, modelType: ModelType): boolean {
  const normalized = modelName.toLowerCase()
  if (modelType === 'embedding') {
    return inferEmbeddingDimension(modelName) !== null
      || normalized.includes('embedding')
      || normalized.includes('embed')
  }
  if (modelType === 'rerank') {
    return normalized.includes('rerank') || normalized.includes('ranker')
  }
  return false
}

function parseVirtualModelId(modelId: string): { provider: string; modelName: string } | null {
  const [provider, ...modelParts] = modelId.split('::')
  const modelName = modelParts.join('::')
  if (!provider || !modelName) {
    return null
  }
  return { provider, modelName }
}

export function buildSelectedModelRef(modelId: string | undefined, models: ModelConfigBrief[]): {
  id: string
  provider?: string
  model: string
  name?: string
} | undefined {
  if (!modelId) {
    return undefined
  }
  const selected = models.find(model => model.id === modelId)
  const parsed = parseVirtualModelId(modelId)
  return {
    id: modelId,
    provider: selected?.provider ?? parsed?.provider,
    model: selected?.modelName ?? parsed?.modelName ?? modelId,
    name: selected?.name,
  }
}

// ========================= 模型配置 API =========================
export const modelConfigsApi = {
  /**
   * 获取模型配置列表
   * @param modelType 模型类型筛选 (embedding/llm/rerank/sparse/multimodal)
   */
  async list(params?: {
    modelType?: ModelType
    provider?: string
    status?: 'active' | 'inactive'
    isDefault?: boolean
    keyword?: string
  }): Promise<{ items: ModelConfigBrief[]; total: number; requiredEmbeddingDimension: number | null }> {
    const catalogItems = await apiClient.get<CatalogModelResponse[]>(
      '/model-config/catalog',
      {
        params: {
          provider: params?.provider,
          model_type: params?.modelType,
          is_enabled: params?.status === 'active'
            ? true
            : params?.status === 'inactive'
              ? false
              : undefined,
          available_only: false,
        }
      }
    )
    const keyword = params?.keyword?.trim().toLowerCase()
    const items = catalogItems
      .map(mapCatalogModelBrief)
      .filter(item => !params?.modelType || item.modelType === params.modelType)
      .filter(item => !keyword
        || item.name.toLowerCase().includes(keyword)
        || item.modelName.toLowerCase().includes(keyword))
      .filter(item => params?.isDefault === undefined || item.isDefault === params.isDefault)
    return {
      items,
      total: items.length,
      requiredEmbeddingDimension: null
    }
  },

  /**
   * 获取 embedding 模型列表
   *
   * 多维方案下知识库维度由用户在创建页选择（embeddingDimension），不再存在
   * "全局唯一维度"。此处返回所有可识别的 embedding 模型（含各自 dimensions），
   * 由 IndexingSettingsStep 按当前 KB 维度做二次过滤。
   *
   * @deprecated `options.filterByRequiredDimension` 已停用，保留参数仅为兼容旧调用。
   */
  async listEmbeddingModels(options?: {
    filterByRequiredDimension?: boolean
  }): Promise<{
    items: ModelConfigBrief[]
    requiredDimension: number | null
    incompatible: ModelConfigBrief[]
  }> {
    void options
    const persistedResult = await this.list({ modelType: 'embedding', status: 'active' })
    return {
      items: persistedResult.items,
      requiredDimension: null,
      incompatible: [],
    }
  },

  /**
   * 获取 rerank 模型列表
   */
  async listRerankModels(): Promise<ModelConfigBrief[]> {
    const result = await this.list({ modelType: 'rerank', status: 'active' })
    return result.items
  },

  /**
   * provider 目录选项不再自动落库；返回运行时模型引用，后端会从 provider 配置解析。
   */
  async ensureModelConfigId(
    modelId: string | undefined,
    modelType: 'embedding' | 'rerank',
    options?: { dimensions?: number | null }
  ): Promise<string | undefined> {
    if (!modelId) {
      return undefined
    }
    void modelType
    void options
    return modelId
  },

  /**
   * 默认模型来自 provider/model 列表的 isDefault 标记，不再请求独立 default 接口。
   */
  async getDefault(modelType: ModelType): Promise<ModelConfigBrief | null> {
    void modelType
    return null
  }
}

// ========================= 资源标签 API =========================
export const resourceTagsApi = {
  /**
   * 获取知识库下所有标签
   * @param knowledgeBaseId 知识库ID
   */
  async list(knowledgeBaseId: string): Promise<ResourceTagItem[]> {
    const response = await apiClient.get<{ items: ResourceTagItem[]; total: number }>(
      `${BASE_PATH}/resources/tags/list`,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response.items
  },

  /**
   * 创建标签
   * @param knowledgeBaseId 知识库ID
   * @param data 标签数据
   */
  async create(
    knowledgeBaseId: string,
    data: { name: string; color?: string; description?: string }
  ): Promise<ResourceTagItem> {
    const response = await apiClient.post<ResourceTagItem>(
      `${BASE_PATH}/resources/tags`,
      data,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 更新标签
   * @param knowledgeBaseId 知识库ID
   * @param tagId 标签ID
   * @param data 更新数据
   */
  async update(
    knowledgeBaseId: string,
    tagId: string,
    data: { name?: string; color?: string; description?: string }
  ): Promise<ResourceTagItem> {
    const response = await apiClient.put<ResourceTagItem>(
      `${BASE_PATH}/resources/tags/${tagId}`,
      data,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 删除标签
   * @param knowledgeBaseId 知识库ID
   * @param tagId 标签ID
   */
  async delete(knowledgeBaseId: string, tagId: string): Promise<boolean> {
    await apiClient.delete(
      `${BASE_PATH}/resources/tags/${tagId}`,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return true
  },

  /**
   * 获取资源的标签
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   */
  async getResourceTags(knowledgeBaseId: string, resourceId: string): Promise<ResourceTagItem[]> {
    try {
      const response = await apiClient.get<ResourceTagItem[] | { items: ResourceTagItem[] }>(
        `${BASE_PATH}/resources/${resourceId}/tags`,
        {
          headers: {
            'X-Knowledge-Id': knowledgeBaseId
          }
        }
      )
      // 响应可能是数组或包含 items 的对象
      if (Array.isArray(response)) {
        return response
      }
      if (response && typeof response === 'object' && 'items' in response) {
        return response.items
      }
      return []
    } catch (error) {
      logger.error('获取资源标签失败:', error)
      return []
    }
  },

  /**
   * 更新资源的标签（替换所有）
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   * @param tagIds 标签ID列表
   */
  async updateResourceTags(
    knowledgeBaseId: string,
    resourceId: string,
    tagIds: string[]
  ): Promise<ResourceTagItem[]> {
    const response = await apiClient.put<ResourceTagItem[]>(
      `${BASE_PATH}/resources/${resourceId}/tags`,
      { tag_ids: tagIds },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 给资源添加单个标签
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   * @param tagId 标签ID
   */
  async addTagToResource(
    knowledgeBaseId: string,
    resourceId: string,
    tagId: string
  ): Promise<boolean> {
    await apiClient.post(
      `${BASE_PATH}/resources/${resourceId}/tags/${tagId}`,
      {},
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return true
  },

  /**
   * 从资源移除单个标签
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   * @param tagId 标签ID
   */
  async removeTagFromResource(
    knowledgeBaseId: string,
    resourceId: string,
    tagId: string
  ): Promise<boolean> {
    await apiClient.delete(
      `${BASE_PATH}/resources/${resourceId}/tags/${tagId}`,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return true
  },

  /**
   * 获取带标签的资源列表
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async getResourcesWithTags(
    knowledgeBaseId: string,
    params?: {
      page?: number
      pageSize?: number
      keyword?: string
      resourceType?: string
      processingStatus?: string
    }
  ): Promise<{
    items: ResourceItem[]
    total: number
    page: number
    page_size: number
  }> {
    const response = await apiClient.get<{
      items: ResourceItem[]
      total: number
      page: number
      page_size: number
    }>(
      `${BASE_PATH}/resources/with-tags`,
      {
        params: {
          page: params?.page || 1,
          page_size: params?.pageSize || 20,
          keyword: params?.keyword,
          resource_type: params?.resourceType,
          processing_status: params?.processingStatus
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  }
}

// ========================= 导出 API =========================
export interface ExportResponse {
  url: string
  file_name: string
  file_size: number
  record_count: number
  exported_at: string
  format?: 'json' | 'excel'
}

export interface KnowledgeBaseExportRequest {
  format: 'json' | 'excel'
}

export interface KnowledgeBaseImportRequest {
  fileUrl: string
  format: 'json' | 'excel'
  name?: string
  groupId?: string
  conflictStrategy?: 'rename'
}

export interface KnowledgeBaseImportResponse {
  knowledge_base_id: string
  knowledge_base_name: string
  success_count: number
  fail_count: number
  errors: string[]
  imported_at: string
}

export const exportApi = {
  /**
   * 导出资源内容
   * @param knowledgeBaseId 知识库ID
   * @param resourceId 资源ID
   */
  async exportResource(
    knowledgeBaseId: string,
    resourceId: string
  ): Promise<ExportResponse> {
    const response = await apiClient.post<ExportResponse>(
      `${BASE_PATH}/export`,
      { resource_id: resourceId },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 导出单个知识库
   * @param knowledgeBaseId 知识库ID
   * @param data 导出选项
   */
  async exportKnowledgeBase(
    knowledgeBaseId: string,
    data: KnowledgeBaseExportRequest
  ): Promise<ExportResponse> {
    const response = await apiClient.post<ExportResponse>(
      `${BASE_PATH}/knowledge-bases/${knowledgeBaseId}/export`,
      { format: data.format },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 导入单个知识库
   * @param data 导入文件与目标配置
   */
  async importKnowledgeBase(data: KnowledgeBaseImportRequest): Promise<KnowledgeBaseImportResponse> {
    const response = await apiClient.post<KnowledgeBaseImportResponse>(
      `${BASE_PATH}/knowledge-bases/import`,
      {
        file_url: data.fileUrl,
        format: data.format,
        name: data.name,
        group_id: data.groupId,
        conflict_strategy: data.conflictStrategy ?? 'rename'
      }
    )
    return response
  }
}

// ========================= 同步 API =========================

// 同步配置
export interface SyncConfig {
  conflict_strategy: 'overwrite' | 'skip' | 'mark'
  delete_strategy: 'sync_delete' | 'soft_delete' | 'keep'
}

// 同步结果
export interface SyncResult {
  success: boolean
  total_checked: number
  created_count: number
  updated_count: number
  skipped_count: number
  failed_count: number
  errors: string[]
  started_at: string
  finished_at: string
  duration_ms: number
}

// 同步状态
export interface SyncStatus {
  source_type: string
  source_name: string
  last_sync_at?: string
  last_sync_result?: SyncResult
  total_synced: number
  pending_updates: number  // -1 表示无法获取（连接失败）
  connection_error?: boolean  // 是否有连接错误
  config: SyncConfig
}

// 单条同步结果
export interface SyncSingleResult {
  success: boolean
  action: 'created' | 'updated' | 'skipped' | 'failed'
  resource_id?: string
  message: string
}

// 文档同步状态枚举
export type ArticleSyncStatus = 'not_synced' | 'synced' | 'updated' | 'deleted'

// 源文档列表项
export interface ArticleListItem {
  article_id: number
  title: string
  updated_at?: string
  updated_by?: string
  sync_status: ArticleSyncStatus
  resource_id?: string
  synced_at?: string
  is_locked: boolean
}

// 源文档列表响应
export interface ArticleListResponse {
  items: ArticleListItem[]
  total: number
  page: number
  page_size: number
  not_synced_count: number
  updated_count: number
  synced_count: number
  deleted_count: number
}

// 源文档列表查询参数
export interface ArticleListParams {
  page?: number
  page_size?: number
  status?: 'all' | 'not_synced' | 'synced' | 'updated' | 'deleted'
  keyword?: string
  include_disabled?: boolean
}

// 删除资源请求
export interface DeleteResourcesRequest {
  resource_ids: string[]
}

// 删除资源结果
export interface DeleteResourcesResult {
  success: boolean
  deleted_count: number
  failed_count: number
  errors: string[]
}

export const syncApi = {
  /**
   * 触发 docs_server 同步
   * @param knowledgeBaseId 知识库ID
   * @param force 是否强制全量同步
   * @param includeDisabled 是否包含禁用的文档（use_status=0）
   */
  async triggerDocsServerSync(
    knowledgeBaseId: string,
    force: boolean = false,
    includeDisabled: boolean = false
  ): Promise<SyncResult> {
    const response = await apiClient.post<SyncResult>(
      `${BASE_PATH}/sync/docs-server`,
      { force, include_disabled: includeDisabled },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        },
        timeout: 300000 // 5 分钟，同步可能需要处理多模态图片
      }
    )
    return response
  },

  /**
   * 获取 docs_server 同步状态
   * @param knowledgeBaseId 知识库ID
   * @param includeDisabled 是否包含禁用的文档（use_status=0）
   * @param checkPending 是否查询待同步更新数（需要连接 docs_server，默认 true）
   */
  async getDocsServerSyncStatus(
    knowledgeBaseId: string,
    includeDisabled: boolean = false,
    checkPending: boolean = true
  ): Promise<SyncStatus> {
    const response = await apiClient.get<SyncStatus>(
      `${BASE_PATH}/sync/docs-server/status`,
      {
        params: {
          include_disabled: includeDisabled,
          check_pending: checkPending
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 同步单条 docs_server 记录
   * @param knowledgeBaseId 知识库ID
   * @param articleId article_info.id
   * @param force 是否强制覆盖（即使被锁定）
   */
  async syncSingleArticle(
    knowledgeBaseId: string,
    articleId: number,
    force: boolean = false
  ): Promise<SyncSingleResult> {
    const response = await apiClient.post<SyncSingleResult>(
      `${BASE_PATH}/sync/docs-server/${articleId}`,
      null,
      {
        params: { force },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 获取源文档列表（精细化同步管理）
   * @param knowledgeBaseId 知识库ID
   * @param params 查询参数
   */
  async listDocsServerArticles(
    knowledgeBaseId: string,
    params: ArticleListParams = {}
  ): Promise<ArticleListResponse> {
    const response = await apiClient.get<ArticleListResponse>(
      `${BASE_PATH}/sync/docs-server/articles`,
      {
        params: {
          page: params.page ?? 1,
          page_size: params.page_size ?? 20,
          status: params.status,
          keyword: params.keyword,
          include_disabled: params.include_disabled ?? false
        },
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        }
      }
    )
    return response
  },

  /**
   * 批量同步指定文档
   * @param knowledgeBaseId 知识库ID
   * @param articleIds 要同步的文章ID列表
   * @param includeDisabled 是否包含禁用的文档
   */
  async syncSelectedArticles(
    knowledgeBaseId: string,
    articleIds: number[],
    includeDisabled: boolean = false
  ): Promise<SyncResult> {
    const response = await apiClient.post<SyncResult>(
      `${BASE_PATH}/sync/docs-server`,
      { 
        article_ids: articleIds,
        include_disabled: includeDisabled 
      },
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        },
        timeout: 300000 // 5 分钟
      }
    )
    return response
  },

  /**
   * 删除指定资源
   * @param knowledgeBaseId 知识库ID
   * @param resourceIds 要删除的资源ID列表
   */
  async deleteResources(
    knowledgeBaseId: string,
    resourceIds: string[]
  ): Promise<DeleteResourcesResult> {
    const response = await apiClient.delete<DeleteResourcesResult>(
      `${BASE_PATH}/sync/docs-server/resources`,
      {
        headers: {
          'X-Knowledge-Id': knowledgeBaseId
        },
        data: { resource_ids: resourceIds }
      }
    )
    return response
  }
}

// ========================= 导出统一入口 =========================
export default {
  groups: groupsApi,
  tags: tagsApi,
  knowledgeBases: knowledgeBasesApi,
  modelConfigs: modelConfigsApi,
  resourceTags: resourceTagsApi,
  export: exportApi,
  sync: syncApi
}

